import { UiConfigType, embedDashboard } from './prebuilt/prebuilt-embedded-sdk';
import { Manifest } from './types/manifest';
import {
  ScrollPostMessage,
  SupersetExtendedSdkWindow,
} from './types/extended-embedded-sdk';

// Provide extended Superset SDK
const w: SupersetExtendedSdkWindow = window;
w.supersetEmbeddedSdk = {
  embedCache: supersetDomain => {
    const supersetCacheEl = document.createElement('superset-cache');
    supersetCacheEl.setAttribute('superset-domain', supersetDomain);
    document.body.appendChild(supersetCacheEl);
  },
  embedDashboard: (...args) => {
    if (!w.supersetEmbeddedSdk) {
      throw new Error('Superset Embedded SDK is not defined');
    }
    w.supersetEmbeddedSdk.isSdkCalled = true;
    return embedDashboard(...args);
  },
  isSdkCalled: false,
};

class SupersetCache extends HTMLElement {
  constructor() {
    super();

    this.attachShadow({ mode: 'open' });
  }

  public connectedCallback(): void {
    document.addEventListener('DOMContentLoaded', () => this.init());
  }

  private async init(): Promise<void> {
    // Check if SDK is called so it means that scripts have been already loading
    if (w.supersetEmbeddedSdk?.isSdkCalled) {
      return;
    }

    const supersetDomain = this.getAttribute('superset-domain');
    if (!supersetDomain) {
      throw new Error(
        'The required "superset-domain" attribute is not defined for "superset-cache" tag.',
      );
    }

    // Fetch manifest to get dynamic names of scripts
    const manifestResponse = await fetch(
      `${supersetDomain}/static/assets/manifest.json?v=${Date.now()}`,
    );
    if (!manifestResponse.ok) {
      throw new Error('Cannot load "manifest.json" for "superset-cache" tag.');
    }
    const manifest = (await manifestResponse.json()) as Manifest;

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    this.shadowRoot?.appendChild(iframe);

    // Check iframe window. It can be null because of unmounting or configuration issues.
    if (!iframe.contentWindow) {
      return;
    }

    const themeScript = iframe.contentWindow.document.createElement('script');
    themeScript.async = true;
    themeScript.src = `${supersetDomain}${manifest.entrypoints.theme.js[0]}`;

    const embeddedScript =
      iframe.contentWindow.document.createElement('script');
    embeddedScript.async = true;
    embeddedScript.src = `${supersetDomain}${manifest.entrypoints.embedded.js[0]}`;

    // Preload required Superset resources
    iframe.contentWindow.document.head.innerHTML =
      `<link href="${supersetDomain}/static/appbuilder/css/flags/flags16.css" rel="stylesheet" type="text/css">` +
      `<link href="${supersetDomain}/static/appbuilder/css/fontawesome/fontawesome.min.css" rel="stylesheet" type="text/css">` +
      `<link href="${supersetDomain}/static/appbuilder/css/fontawesome/regular.min.css" rel="stylesheet" type="text/css">` +
      `<link href="${supersetDomain}/static/appbuilder/css/fontawesome/solid.min.css" rel="stylesheet" type="text/css">` +
      `<link href="${supersetDomain}/static/appbuilder/css/fontawesome/brands.min.css" rel="stylesheet" type="text/css">` +
      `<link href="${supersetDomain}/static/appbuilder/css/bootstrap-datepicker/bootstrap-datepicker3.min.css" rel="stylesheet" type="text/css">` +
      `<link href="${supersetDomain}/static/appbuilder/css/select2/select2.min.css" rel="stylesheet" type="text/css">` +
      `<link href="${supersetDomain}/static/appbuilder/css/select2/select2-bootstrap.min.css" rel="stylesheet" type="text/css">`;
    // Scripts should be added separately because they cannot be loaded via innerHTML
    iframe.contentWindow.document.head.appendChild(themeScript);
    iframe.contentWindow.document.head.appendChild(embeddedScript);
    iframe.contentWindow.document.body.innerHTML = `<img src="${supersetDomain}/static/assets/images/loading.gif">`;
  }
}

class SupersetDashboard extends HTMLElement {
  private isInitialFetchOfGuestToken = true;

  private resizingIntervalId?: NodeJS.Timeout;

  private lastSentTop = 0;

  constructor() {
    super();

    this.attachShadow({ mode: 'open' });
  }

  private onScroll = () => {
    const iframes = this.shadowRoot?.querySelectorAll('iframe');

    iframes?.forEach(iframe => {
      if (!iframe?.contentWindow) return;

      const iframeRect = iframe?.getBoundingClientRect();
      const roundedTop = Math.round(iframeRect.top * 10) / 10;
      const { scrollY, innerHeight } = window;

      if (roundedTop !== this.lastSentTop) {
        const message: ScrollPostMessage = {
          type: 'scroll',
          payload: { iframeRect, scrollY, parentViewportHeight: innerHeight },
        };
        iframe?.contentWindow?.postMessage(message, iframe?.src);
        this.lastSentTop = roundedTop;
      }
    });

    requestAnimationFrame(this.onScroll);
  };

  private onTabChange = (e: MessageEvent) => {
    if (e.data?.type === 'EMPIRE_SET_ACTIVE_TAB') {
      const iframes = this.shadowRoot?.querySelectorAll('iframe');

      iframes?.forEach(iframe => {
        if (!iframe?.contentWindow) return;
        iframe?.contentWindow?.postMessage(e.data, iframe?.src);
      });
    }
  };

  public async connectedCallback(): Promise<void> {
    if (!w.supersetEmbeddedSdk) {
      throw new Error('Superset Embedded SDK is not defined');
    }

    const styleEl = document.createElement('style');
    styleEl.innerHTML = 'iframe{border:0;height:100%;width:100%}';
    this.shadowRoot?.appendChild(styleEl);

    const mountPoint = document.createElement('div');
    this.shadowRoot?.appendChild(mountPoint);

    const dashboard = await w.supersetEmbeddedSdk.embedDashboard({
      dashboardUiConfig: {
        hideChartControls: false,
        hideTitle: true,
        ...this.parseJsonAttribute<UiConfigType>('dashboard-ui-config'),
      },
      fetchGuestToken: () => this.fetchGuestToken(),
      id: this.getAttribute('id') ?? '',
      mountPoint,
      supersetDomain: this.getAttribute('superset-domain') ?? '',
    });

    const resize = async (): Promise<void> => {
      const scrollSize = await dashboard.getScrollSize();
      // Add 20px to prevent issues with appeared/disappeared scroll bar
      mountPoint.style.height = `${scrollSize.height + 20}px`;
    };
    await resize();
    this.resizingIntervalId = setInterval(resize, 1000);

    requestAnimationFrame(this.onScroll);

    window.addEventListener('message', this.onTabChange);
  }

  public disconnectedCallback(): void {
    if (this.resizingIntervalId) {
      clearInterval(this.resizingIntervalId);
    }

    window.removeEventListener('message', this.onTabChange);
  }

  private async fetchGuestToken(): Promise<string> {
    const initialGuestToken = this.getAttribute('guest-token');
    if (initialGuestToken && this.isInitialFetchOfGuestToken) {
      this.isInitialFetchOfGuestToken = false;
      return initialGuestToken;
    }

    const guestTokenUrl = this.getAttribute('guest-token-url');
    if (!guestTokenUrl) {
      throw new Error(
        'Cannot fetch guest token for superset-dashboard because "guest-token-url" attribute is not defined',
      );
    }
    const id = this.getAttribute('id') ?? '';
    try {
      const raw = this.getAttribute('guest-token-dashboard-ids');
      if (!raw) {
        return this.fetchGuestTokenById(guestTokenUrl, id);
      }
      const ids = this.parseDashboardIds(raw);
      return await this.fetchGuestTokenById(
        guestTokenUrl,
        ids.length ? ids : id,
      );
    } catch (e) {
      // Log the error
      // eslint-disable-next-line no-console
      console.error(e);
      // Retry using only one id
      return this.fetchGuestTokenById(guestTokenUrl, id);
    }
  }

  private async fetchGuestTokenById(
    url: string,
    id: string | string[],
  ): Promise<string> {
    const ids = typeof id === 'string' ? [id] : id;

    const params = new URLSearchParams();
    ids.forEach(d => params.append('superset_dashboard_ids[]', d));

    const response = await fetch(url, {
      body: params,
      cache: 'no-store',
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`Cannot fetch guest token for superset-dashboard`);
    }
    const data = await response.json();
    return data.guest_token ?? '';
  }

  private parseDashboardIds(value: string): string[] {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {
      // ignore and fallback
    }
    return Array.from(value.matchAll(/'([^']+)'/g), m => m[1]);
  }

  private parseJsonAttribute<T>(attributeName: string): T | undefined {
    try {
      const value = this.getAttribute(attributeName);
      return value ? (JSON.parse(decodeURIComponent(value)) as T) : undefined;
    } catch (e) {
      // Log error
      // eslint-disable-next-line no-console
      console.error(
        `Cannot parse JSON from superset-dashboard attribute ${attributeName}:`,
        e,
      );
      return undefined;
    }
  }
}

((): void => {
  customElements.define('superset-cache', SupersetCache);
  customElements.define('superset-dashboard', SupersetDashboard);
})();
